/**
 * A person's own grants and deductions (legacy "employee bonus cuts"): fixed amounts
 * or formulas paid on top of the company-wide rules on Payroll → Components.
 */
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api, get, patch, post } from '../lib/api.ts';
import { idr } from '../lib/format.ts';
import { Button, Callout, Chip, Dialog, Field, Panel, Segmented, Skeleton, Switch, useConfirm, useToast } from './ui.tsx';

interface Own {
  id: string;
  componentId: string;
  amount: string | null;
  useFormula: boolean;
  formula: string | null;
  active: boolean;
  notes: string | null;
  component: { id: string; code: string; name: string; kind: 'earning' | 'deduction'; role: string | null };
}
interface Catalogue {
  id: string;
  code: string;
  name: string;
  kind: 'earning' | 'deduction';
  role: string | null;
  manual: boolean;
  rules: { amount: string | null; useFormula: boolean; active: boolean; officeId: string | null }[];
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');
/** What the API takes from what people type: "400.000" (dots for thousands), "400000", "400000,50" or "400000.50". */
const toAmount = (typed: string) => {
  const t = typed.replace(/\s|rp/gi, '');
  return /^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(t) ? t.replace(/\./g, '').replace(',', '.') : t.replace(',', '.');
};
const sumOf = (rows: Own[], kind: 'earning' | 'deduction') => rows.filter((r) => r.active && !r.useFormula && r.component.kind === kind).reduce((n, r) => n + Number(r.amount ?? 0), 0);

export function PersonComponents({ employeeId, name, canEdit, canSimulate }: { employeeId: string; name: string; canEdit: boolean; canSimulate: boolean }) {
  const q = useQuery({ queryKey: ['person-components', employeeId], queryFn: () => get<Own[]>(`/api/payroll/employees/${employeeId}/components`) });
  const [editing, setEditing] = useState<Own | 'new' | null>(null);
  const rows = [...(q.data ?? [])].sort((a, b) => (a.component.kind === b.component.kind ? a.component.name.localeCompare(b.component.name) : a.component.kind === 'earning' ? -1 : 1));
  const plus = sumOf(rows, 'earning');
  const minus = sumOf(rows, 'deduction');
  return (
    <Panel
      eyebrow="Salary"
      title="Grants & deductions"
      flush
      action={
        <div className="row" style={{ gap: 4 }}>
          {canSimulate ? (
            <Link to="/payroll/simulate" search={{ employeeId }} className="btn btn--ghost btn--sm">
              Simulate salary
            </Link>
          ) : null}
          {canEdit ? (
            <Button size="sm" icon="plus" onClick={() => setEditing('new')}>
              Add
            </Button>
          ) : null}
        </div>
      }
    >
      {q.isLoading ? (
        <div style={{ padding: 'var(--s-4)' }}>
          <Skeleton h={120} />
        </div>
      ) : rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>Component</th>
              <th className="right">Per period</th>
              <th>Notes</th>
              {canEdit ? <th aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                <td>
                  <span className="row" style={{ ['--gap' as string]: '8px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: r.component.kind === 'earning' ? 'var(--sage)' : 'var(--clay)', flex: 'none' }} aria-hidden />
                    <span>
                      {r.component.name} {!r.active ? <Chip>Off</Chip> : null}
                      <span className="mono faint" style={{ display: 'block', fontSize: 10.5 }}>
                        {r.component.code} · {r.component.kind === 'earning' ? 'earning' : 'deduction'}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="right num">{r.useFormula ? <span className="mono faint" title={r.formula ?? ''}>ƒ formula</span> : `${r.component.kind === 'deduction' ? '−' : ''}${idr(r.amount)}`}</td>
                <td className="faint" style={{ fontSize: 'var(--step--1)' }}>{r.notes ?? ''}</td>
                {canEdit ? (
                  <td className="right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                      Edit
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="faint">Fixed amounts, active</td>
              <td className="right num" colSpan={canEdit ? 3 : 2}>
                +{idr(plus)} · −{idr(minus)}
              </td>
            </tr>
          </tfoot>
        </table>
      ) : (
        <p className="faint" style={{ padding: 'var(--s-4)', fontSize: 'var(--step--1)' }}>
          None of {name}'s own. Company-wide components still apply.
        </p>
      )}
      <p className="faint" style={{ padding: '10px var(--s-4) var(--s-4)', fontSize: 'var(--step--2)' }}>
        Paid on top of the company-wide rules on <Link to="/payroll/components" className="link">Payroll → Components</Link>. Changes count from the next calculation.
      </p>
      {editing ? <OwnDialog employeeId={employeeId} name={name} row={editing === 'new' ? null : editing} taken={rows.map((r) => r.componentId)} onClose={() => setEditing(null)} /> : null}
    </Panel>
  );
}

function OwnDialog({ employeeId, name, row, taken, onClose }: { employeeId: string; name: string; row: Own | null; taken: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const catalogue = useQuery({ queryKey: ['components', false], queryFn: () => get<Catalogue[]>('/api/payroll/components') });
  const [componentId, setComponentId] = useState(row?.componentId ?? '');
  const [mode, setMode] = useState<'amount' | 'formula'>(row?.useFormula ? 'formula' : 'amount');
  const [amount, setAmount] = useState(row?.amount?.replace(/\.00$/, '') ?? '');
  const [formula, setFormula] = useState(row?.formula ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [active, setActive] = useState(row?.active ?? true);
  const [check, setCheck] = useState<{ ok: boolean; error?: { message: string; position: number } } | null>(null);

  useEffect(() => {
    if (mode !== 'formula' || !formula.trim()) return setCheck(null);
    const t = setTimeout(async () => {
      try {
        setCheck(await post('/api/payroll/formula/check', { source: formula }));
      } catch {
        setCheck(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [formula, mode]);

  const chosen = catalogue.data?.find((c) => c.id === componentId) ?? (row ? { ...row.component, manual: false, rules: [] } : undefined);
  const everyone = chosen?.rules.find((r) => !r.officeId && r.active);
  const options = (catalogue.data ?? []).filter((c) => !taken.includes(c.id) || c.id === row?.componentId);
  const done = (title: string) => {
    toast({ title });
    void qc.invalidateQueries({ queryKey: ['person-components', employeeId] });
    onClose();
  };
  const value = { amount: mode === 'amount' ? toAmount(amount) || null : null, useFormula: mode === 'formula', formula: mode === 'formula' ? formula : null };
  const save = useMutation({
    mutationFn: () => (row ? patch(`/api/payroll/employees/${employeeId}/components/${row.id}`, { ...value, active, notes: notes.trim() || null }) : post(`/api/payroll/employees/${employeeId}/components`, { componentId, ...value, notes: notes.trim() || undefined })),
    onSuccess: () => done(row ? `${chosen?.name ?? 'Component'} saved` : `${chosen?.name ?? 'Component'} added for ${name}`),
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/payroll/employees/${employeeId}/components/${row!.id}`, { method: 'DELETE' }),
    onSuccess: () => done(`${row!.component.name} removed`),
  });
  const ready = (row || componentId) && (mode === 'amount' ? /^\d+(\.\d{1,2})?$/.test(value.amount ?? '') : !!check?.ok);
  const group = (kind: 'earning' | 'deduction') => options.filter((c) => c.kind === kind);
  return (
    <>
      <Dialog
        open
        onClose={onClose}
        eyebrow={name}
        title={row ? row.component.name : 'Add a grant or deduction'}
        width={560}
        footer={
          <>
            {row ? (
              <Button
                variant="ghost"
                loading={remove.isPending}
                onClick={async () => {
                  if (await confirm.ask(`Remove ${row.component.name}?`, `${name} stops getting it from the next calculation. Payslips already calculated keep it.`, 'Remove', 'danger')) remove.mutate();
                }}
              >
                Remove
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
              {row ? 'Save' : 'Add'}
            </Button>
          </>
        }
      >
        <div className="grid-12" style={{ gap: 14 }}>
          {!row ? (
            <div className="col-12">
              <Field label="Component">
                {(p) => (
                  <select {...p} className="select" value={componentId} onChange={(e) => setComponentId(e.target.value)}>
                    <option value="">Choose…</option>
                    <optgroup label="Earnings (grants)">
                      {group('earning').map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                    <optgroup label="Deductions">
                      {group('deduction').map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  </select>
                )}
              </Field>
            </div>
          ) : null}
          {everyone ? (
            <div className="col-12">
              <Callout tone="warn" title="Everyone already gets this.">
                {chosen!.name} has a company-wide {everyone.useFormula ? 'formula' : `amount of ${idr(everyone.amount)}`}. An amount here is paid on top of it, not instead.
              </Callout>
            </div>
          ) : null}
          <div className="col-12">
            <Segmented label="Value type" idPrefix="own-mode" value={mode} onChange={setMode} options={[{ id: 'amount', label: 'Fixed amount' }, { id: 'formula', label: 'Formula' }]} />
          </div>
          <div className="col-12">
            {mode === 'amount' ? (
              <Field label="Amount per period" hint={value.amount && /^\d+(\.\d{1,2})?$/.test(value.amount) ? idr(value.amount) : 'Rupiah'}>
                {(p) => <input {...p} className="input input--mono" inputMode="decimal" value={amount} placeholder="250000" onChange={(e) => setAmount(e.target.value)} />}
              </Field>
            ) : (
              <Field label="Formula" hint="Same language as on Payroll → Components" error={check && !check.ok ? `${check.error?.message} (at character ${(check.error?.position ?? 0) + 1})` : null}>
                {(p) => <textarea {...p} className="textarea" rows={3} spellCheck={false} value={formula} onChange={(e) => setFormula(e.target.value)} />}
              </Field>
            )}
          </div>
          <div className="col-12">
            <Field label="Notes" hint="Optional">{(p) => <input {...p} className="input" value={notes} maxLength={250} onChange={(e) => setNotes(e.target.value)} />}</Field>
          </div>
          {row ? (
            <div className="col-12">
              <Switch checked={active} onChange={setActive} label="Active (switch off to pause it without removing)" showLabel />
            </div>
          ) : null}
          {save.error || remove.error ? (
            <div className="col-12">
              <Callout tone="bad">{errText(save.error ?? remove.error)}</Callout>
            </div>
          ) : null}
        </div>
      </Dialog>
      {confirm.node}
    </>
  );
}
